import type { HTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils/cn";

export const badgeVariants = cva(
  "inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium leading-4",
  {
    variants: {
      variant: {
        default: "border-transparent bg-surface-2 text-fg",
        primary: "border-transparent bg-primary text-primary-fg",
        accent: "border-transparent bg-accent text-accent-fg",
        outline: "border-border bg-transparent text-fg",
        success: "border-transparent bg-success/15 text-success",
        warning: "border-transparent bg-warning/15 text-warning",
        danger: "border-transparent bg-danger/15 text-danger",
        run: "border-transparent bg-sport-run/15 text-sport-run",
        walk: "border-transparent bg-sport-walk/15 text-sport-walk",
        cycle: "border-transparent bg-sport-cycle/15 text-sport-cycle",
        swim: "border-transparent bg-sport-swim/15 text-sport-swim",
        gold: "border-transparent bg-rank-gold/20 text-rank-gold",
        podium: "border-transparent bg-rank-podium/20 text-rank-podium",
        top10: "border-transparent bg-rank-top10/15 text-rank-top10",
        none: "border-transparent bg-rank-none/15 text-rank-none",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>["variant"]>;
export type BadgeProps = HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants>;

export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

/** Maps a normalised sport string to a badge variant. */
export function sportBadgeVariant(sport: string | null | undefined): BadgeVariant {
  switch ((sport ?? "").toLowerCase()) {
    case "running":
    case "run":
      return "run";
    case "walking":
    case "walk":
    case "hiking":
      return "walk";
    case "cycling":
    case "ride":
      return "cycle";
    case "swimming":
    case "swim":
      return "swim";
    default:
      return "default";
  }
}
